import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { FiStar, FiDollarSign, FiClock, FiMapPin, FiMessageCircle, FiThumbsUp, FiArrowRight } from 'react-icons/fi'
import { RootState } from '@/store'
import { successStoriesApi } from '@/services/api/successStories'
import { SuccessStory, SuccessStoriesFilters, SuccessStoryCategory } from '@/types/successStory'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import Avatar from '@/components/ui/Avatar'

const SuccessStoriesPage: React.FC = () => {
    const navigate = useNavigate()
    const { isAuthenticated } = useSelector((state: RootState) => state.auth)

    // State management
    const [activeCategory, setActiveCategory] = useState('all')
    const [stories, setStories] = useState<SuccessStory[]>([])
    const [categories, setCategories] = useState<SuccessStoryCategory[]>([])
    const [stats, setStats] = useState<any>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [searchQuery, setSearchQuery] = useState('')

    // Fetch stories
    useEffect(() => {
        const fetchStories = async () => {
            try {
                setLoading(true)
                setError(null)

                const filters: SuccessStoriesFilters = {
                    page: currentPage,
                    limit: 12,
                }

                if (activeCategory !== 'all') {
                    filters.category = activeCategory
                }

                if (searchQuery) {
                    filters.search = searchQuery
                }

                const response = await successStoriesApi.getSuccessStories(filters)

                if (response.data) {
                    setStories(response.data.stories || [])
                    setCategories(response.data.categories || [])
                    setStats(response.data.stats || null)
                    setTotalPages(response.data.totalPages || 1)
                }
            } catch (err: any) {
                setError(err.response?.data?.message || 'Failed to load success stories')
                console.error('Error fetching success stories:', err)
            } finally {
                setLoading(false)
            }
        }

        fetchStories()
    }, [activeCategory, currentPage, searchQuery])

    const handleStoryClick = (id: string) => {
        navigate(`/success-stories/${id}`)
    }

    const handleShareStory = () => {
        if (!isAuthenticated) {
            navigate('/login')
            return
        }
        navigate('/success-stories/submit')
    }

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1)
            window.scrollTo({ top: 0, behavior: 'smooth' })
        }
    }

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1)
            window.scrollTo({ top: 0, behavior: 'smooth' })
        }
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Hero Section */}
            <section className="bg-gray-100 dark:bg-gray-800 py-12 sm:py-16 md:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl md:heading-hero text-gray-900 dark:text-white mb-4 sm:mb-6">Success Stories</h1>
                    <p className="text-base sm:text-lg md:text-body-lg text-gray-700 dark:text-gray-300 max-w-3xl mx-auto mb-6 sm:mb-8">
                        Discover how freelancers and businesses have transformed their careers and
                        achieved their goals through SideGO. These real stories showcase the power
                        of our platform to create meaningful connections and drive success.
                    </p>

                    {isAuthenticated && (
                        <button
                            onClick={handleShareStory}
                            className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors text-sm sm:text-base"
                        >
                            Share Your Story
                            <FiArrowRight className="ml-2 w-4 h-4" />
                        </button>
                    )}
                </div>
            </section>

            {/* Stats Section */}
            {stats && (
                <section className="py-8 sm:py-12 md:py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
                            <div className="text-center">
                                <div className="text-3xl sm:text-4xl font-heading font-bold text-success-600 dark:text-success-400 mb-1 sm:mb-2">
                                    {stats.totalStories}+
                                </div>
                                <div className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                                    Success Stories
                                </div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl sm:text-4xl font-heading font-bold text-success-600 dark:text-success-400 mb-1 sm:mb-2">
                                    {stats.averageEarningsGrowth}%
                                </div>
                                <div className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                                    Avg. Growth
                                </div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl sm:text-4xl font-heading font-bold text-success-600 dark:text-success-400 mb-1 sm:mb-2">
                                    {stats.clientSatisfaction}%
                                </div>
                                <div className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                                    Satisfaction
                                </div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl sm:text-4xl font-heading font-bold text-success-600 dark:text-success-400 mb-1 sm:mb-2">
                                    {stats.repeatClients}%
                                </div>
                                <div className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                                    Repeat Clients
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Search Section */}
            <section className="py-6 sm:py-8 border-b border-gray-200 dark:border-gray-700">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="mb-4 sm:mb-6">
                        <input
                            type="text"
                            placeholder="Search stories..."
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value)
                                setCurrentPage(1)
                            }}
                            className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm sm:text-base"
                        />
                    </div>

                    {/* Filter Tabs */}
                    <div className="flex flex-wrap justify-center gap-2">
                        {categories.map((category) => (
                            <button
                                key={category.id}
                                onClick={() => {
                                    setActiveCategory(category.id)
                                    setCurrentPage(1)
                                }}
                                className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-sm sm:text-base transition-colors duration-200 whitespace-nowrap ${activeCategory === category.id
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                    }`}
                            >
                                {category.label} ({category.count})
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Success Stories Grid */}
            <section className="py-12 sm:py-16 md:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {loading ? (
                        <div className="flex justify-center items-center min-h-96">
                            <LoadingSpinner />
                        </div>
                    ) : error ? (
                        <div className="text-center py-12">
                            <p className="text-sm sm:text-body text-red-600 dark:text-red-400 mb-4">{error}</p>
                            <button
                                onClick={() => window.location.reload()}
                                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm sm:text-base rounded-lg transition-colors"
                            >
                                Retry
                            </button>
                        </div>
                    ) : stories.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-sm sm:text-body text-gray-600 dark:text-gray-400">No success stories found.</p>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8 mb-8">
                                {stories.map((story) => (
                                    <div
                                        key={story._id}
                                        onClick={() => handleStoryClick(story._id)}
                                        className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg sm:rounded-xl p-4 sm:p-6 md:p-8 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer hover:border-primary-500"
                                    >
                                        {/* Profile Header */}
                                        <div className="flex flex-col xs:flex-row xs:items-start mb-4 sm:mb-6 gap-3 xs:gap-4">
                                            <Avatar
                                                avatarId={story.avatar}
                                                alt={story.name}
                                                size="lg"
                                                className="flex-shrink-0"
                                                fallbackIcon={
                                                    <div className="w-full h-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-heading font-bold text-lg">
                                                        {story.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                                    </div>
                                                }
                                            />
                                            <div className="flex-1 min-w-0">
                                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-0">
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="text-lg sm:text-xl md:heading-card text-gray-900 dark:text-white mb-1 truncate font-semibold">{story.name}</h3>
                                                        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-1 sm:mb-2 truncate">{story.role}</p>
                                                        <div className="flex items-center text-sm sm:text-base text-gray-500 dark:text-gray-400 truncate">
                                                            <FiMapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                                                            <span className="truncate">{story.location}</span>
                                                        </div>
                                                    </div>
                                                    <div className="text-right flex-shrink-0">
                                                        <div className="flex items-center justify-end mb-1">
                                                            <FiStar className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400 mr-1 fill-yellow-400" />
                                                            <span className="text-sm sm:text-base font-medium text-gray-900 dark:text-white">{story.rating.toFixed(1)}</span>
                                                        </div>
                                                        <div className="text-sm sm:text-base text-gray-500 dark:text-gray-400">
                                                            {story.projectsCompleted} projects
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Achievement Banner */}
                                        <div className="bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900 dark:to-primary-800 border border-primary-200 dark:border-primary-700 rounded-lg p-2.5 sm:p-4 mb-4 sm:mb-6">
                                            <div className="text-sm sm:text-base font-medium text-primary-900 dark:text-primary-100">
                                                🏆 {story.achievement}
                                            </div>
                                        </div>

                                        {/* Category Badge */}
                                        <div className="mb-3 sm:mb-4">
                                            <span className="inline-block px-2.5 sm:px-3 py-0.5 sm:py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-medium rounded-full capitalize">
                                                {story.category}
                                            </span>
                                        </div>

                                        {/* Story */}
                                        <div className="mb-4 sm:mb-6">
                                            <div className="relative">
                                                <FiMessageCircle className="absolute top-0 left-0 w-4 h-4 sm:w-6 sm:h-6 text-gray-400 dark:text-gray-500 transform -translate-x-1.5 sm:-translate-x-2 -translate-y-1.5 sm:-translate-y-2 flex-shrink-0" />
                                                <p className="text-sm sm:text-base text-body text-gray-700 dark:text-gray-300 italic pl-3 sm:pl-4">
                                                    "{story.testimonial}"
                                                </p>
                                            </div>
                                        </div>

                                        <p className="text-sm sm:text-base text-body text-gray-700 dark:text-gray-300 mb-4 sm:mb-6 line-clamp-3">{story.story}</p>

                                        {/* Skills */}
                                        <div className="mb-4 sm:mb-6">
                                            <div className="flex flex-wrap gap-1.5 sm:gap-2">
                                                {story.skills.slice(0, 4).map((skill, index) => (
                                                    <span key={index} className="inline-block bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs px-2 py-0.5 sm:px-2 sm:py-1 rounded-full">
                                                        {skill}
                                                    </span>
                                                ))}
                                                {story.skills.length > 4 && (
                                                    <span className="inline-block bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs px-2 py-0.5 sm:px-2 sm:py-1 rounded-full">
                                                        +{story.skills.length - 4} more
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Stats */}
                                        <div className="grid grid-cols-2 gap-2 sm:gap-4 pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-700">
                                            <div>
                                                <div className="flex items-center text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1">
                                                    <FiDollarSign className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                                                    <span className="truncate">Total Earnings</span>
                                                </div>
                                                <div className="text-base sm:text-lg font-heading font-semibold text-success-600 dark:text-success-400 truncate">
                                                    {story.totalEarnings}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="flex items-center text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1">
                                                    <FiClock className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                                                    <span className="truncate">Joined</span>
                                                </div>
                                                <div className="text-base sm:text-lg font-heading font-semibold text-gray-900 dark:text-white truncate">
                                                    {story.joinDate}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Engagement Stats */}
                                        <div className="grid grid-cols-2 gap-2 sm:gap-4 pt-3 sm:pt-4">
                                            <div className="text-center py-2 sm:py-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                                <div className="text-sm sm:text-base text-gray-600 dark:text-gray-400">Views</div>
                                                <div className="text-base sm:text-lg text-heading font-semibold text-gray-900 dark:text-white">{story.views}</div>
                                            </div>
                                            <div className="text-center py-2 sm:py-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                                <div className="text-sm sm:text-base text-gray-600 dark:text-gray-400 flex items-center justify-center">
                                                    <FiThumbsUp className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" /> <span className="hidden sm:inline">Helpful</span>
                                                </div>
                                                <div className="text-base sm:text-lg text-heading font-semibold text-gray-900 dark:text-white">{story.helpful}</div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4 mt-8 sm:mt-12">
                                    <button
                                        onClick={handlePreviousPage}
                                        disabled={currentPage === 1}
                                        className="w-full sm:w-auto px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm sm:text-base"
                                    >
                                        Previous
                                    </button>
                                    <span className="text-xs sm:text-ui text-gray-600 dark:text-gray-400">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <button
                                        onClick={handleNextPage}
                                        disabled={currentPage === totalPages}
                                        className="w-full sm:w-auto px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm sm:text-base"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </section>
        </div>
    )
}

export default SuccessStoriesPage
